// useAutosave — schützt User vor Canvas-Datenverlust durch lokales Backup.
//
// Funktion:
//   - hört auf canvas-Events (object:added/modified/removed, canvas:changed)
//   - serialisiert canvas.toJSON() THROTTLED (max 1×/s)
//   - speichert unter localStorage['selenny-designer-autosave-<designId>']
//   - "draft" key falls noch keine designId existiert
//   - exponiert clearAutosave() für expliziten Save-Erfolg
//
// Gespeicherte Struktur: { json, views, savedAt, designId, widthCm, heightCm,
//   productName, finishing, bleedCm, hemCm, grommets, hohlsaum }
import { useEffect, useRef, useCallback } from 'react';
import eventBus from '../core/EventBus';

const THROTTLE_MS = 1000;
const KEY_PREFIX = 'selenny-designer-autosave-';
const DRAFT_KEY = 'selenny-designer-autosave-draft';

export function getAutosaveKey(designId) {
  return designId ? `${KEY_PREFIX}${designId}` : DRAFT_KEY;
}

export function readAutosave(designId) {
  try {
    const raw = localStorage.getItem(getAutosaveKey(designId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.json) return null;
    return data;
  } catch (e) {
    return null;
  }
}

export function clearAutosave(designId) {
  try {
    localStorage.removeItem(getAutosaveKey(designId));
    // Falls draft+designId existieren parallel: beide löschen
    if (designId) localStorage.removeItem(DRAFT_KEY);
  } catch (e) {}
}

/**
 * @param {Object} opts
 * @param {React.MutableRefObject} opts.canvasManagerRef
 * @param {React.MutableRefObject} opts.viewManagerRef
 * @param {boolean} opts.isInitialized
 * @param {number|null} opts.designId
 * @param {Object} opts.meta — { widthCm, heightCm, productName, finishing, bleedCm, hemCm, grommets, hohlsaum }
 */
export default function useAutosave({ canvasManagerRef, viewManagerRef, isInitialized, designId, meta }) {
  const lastSavedRef = useRef(0);
  const pendingTimerRef = useRef(null);
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const doSave = useCallback(() => {
    const cm = canvasManagerRef.current;
    if (!cm || !cm.canvas) return;
    try {
      const json = cm.canvas.toJSON([
        'customName', 'isLocked', 'isGuide', 'guideOrientation', 'guidePositionPx',
        'excludeFromExport',
        '_curvedText', '_curvedType', '_curvedFontSize', '_curvedFontFamily',
        '_curvedFontWeight', '_curvedFill', '_curvedStroke', '_curvedStrokeWidth',
        '_curvedRadiusFactor', '_curvedSpacing',
        'isGrid', 'isBleed', 'isHem', 'isHohlsaum', 'isGrommet', 'isSnapLine',
      ]);
      // Transient/overlay objects raus
      if (json.objects) {
        json.objects = json.objects.filter(
          (o) => !o.isGrid && !o.isBleed && !o.isHem && !o.isHohlsaum &&
                 !o.isGrommet && !o.isSnapLine
        );
      }
      // Wenn Canvas ohne User-Objekte → NICHT speichern (würde sonst potenziell
      // einen guten Autosave mit "leer" überschreiben)
      const hasUserObjects = json.objects && json.objects.length > 0;
      const vm = viewManagerRef.current;
      let viewsHaveContent = false;
      let views = null;
      if (vm && typeof vm.getAllViewsJSON === 'function') {
        try {
          views = vm.getAllViewsJSON();
          viewsHaveContent = (views || []).some(v => {
            if (!v || !v.canvasJSON) return false;
            const cj = typeof v.canvasJSON === 'string' ? JSON.parse(v.canvasJSON) : v.canvasJSON;
            return cj && cj.objects && cj.objects.length > 0;
          });
        } catch {}
      }
      if (!hasUserObjects && !viewsHaveContent) return;

      const payload = {
        json,
        views,
        savedAt: Date.now(),
        designId: designId || null,
        ...(metaRef.current || {}),
      };
      const key = getAutosaveKey(designId);
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (qErr) {
        // QuotaExceededError → wahrscheinlich Bilder zu groß. Versuche ohne backgroundImage.
        try {
          if (payload.json && payload.json.backgroundImage) delete payload.json.backgroundImage;
          localStorage.setItem(key, JSON.stringify(payload));
        } catch (e2) {
          console.warn('[autosave] localStorage quota — autosave skipped:', e2.message);
        }
      }
      lastSavedRef.current = Date.now();
    } catch (e) {
      console.warn('[autosave] save failed:', e);
    }
  }, [canvasManagerRef, viewManagerRef, designId]);

  const scheduleSave = useCallback(() => {
    const now = Date.now();
    const since = now - lastSavedRef.current;
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    if (since >= THROTTLE_MS) {
      doSave();
    } else {
      pendingTimerRef.current = setTimeout(doSave, THROTTLE_MS - since);
    }
  }, [doSave]);

  useEffect(() => {
    if (!isInitialized) return;
    const cm = canvasManagerRef.current;
    if (!cm || !cm.canvas) return;

    // Listener auf zentrales canvas:changed (deckt object:added/modified/removed +
    // path:created via CanvasManager-Eventbridges ab)
    const unsub = eventBus.on('canvas:changed', scheduleSave);

    // Direkter Listener auf canvas falls EventBus mal ausfallen sollte (Backup)
    const onMod = () => scheduleSave();
    cm.canvas.on('object:modified', onMod);
    cm.canvas.on('object:added', onMod);
    cm.canvas.on('object:removed', onMod);
    cm.canvas.on('path:created', onMod);

    return () => {
      if (typeof unsub === 'function') unsub();
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
      try {
        cm.canvas.off('object:modified', onMod);
        cm.canvas.off('object:added', onMod);
        cm.canvas.off('object:removed', onMod);
        cm.canvas.off('path:created', onMod);
      } catch {}
    };
  }, [isInitialized, scheduleSave, canvasManagerRef]);

  return { clearAutosave: () => clearAutosave(designId), forceSave: doSave };
}
